import React, { useContext, useEffect, useState, Fragment } from 'react';
import { AuthContext } from '../contexts/auth';
import { getRateosCompanyList, createRateoEmployee, getRateosEmployeeList, deleteRateoEmployee, updateRateoEmployee } from '../services/api_rateos';
import { getEmployeeContractsList } from '../services/api_contracts_employee';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { ArrowUturnLeftIcon, PencilIcon, PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import moment from 'moment';



const initialInputValues = {
    rateoValue: '',
    dateFrom: '',
    dateTo: '',
};

const formatDate = (dateString) => {
    if (!dateString) {
        return '';
    }

    const dateObject = new Date(dateString);
    const day = ('0' + dateObject.getDate()).slice(-2);
    const month = ('0' + (dateObject.getMonth() + 1)).slice(-2);
    const year = dateObject.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    return formattedDate;
};


export default function RateiTabEmployee({employeeId, setUnsavedChanges}) {
    const { token } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rateiCompany, setRateiCompany] = useState([]);
    const [selectedRateo, setSelectedRateo] = useState('');
    const [selectedRateoId, setSelectedRateoId] = useState();
    const [inputValues, setInputValues] = useState(initialInputValues);
    const [contracts, setContracts] = useState([]);
    const [employeeContract, setEmployeeContract] = useState(null);
    const [rateiEmployee, setRateiEmployee] = useState([]);
    const [activeContractId, setActiveContractId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [isRateoActive, setIsRateoActive] = useState(false);
    const [errorCheck, setErrorCheck] = useState('');
    const [editRateo, setEditRateo] = useState(null);
    const [editRateoId, setEditRateoId] = useState(null);
    const [showFormEdit, setShowFormEdit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        date_from: '',
        date_to: '',
        monthly_increase: 0,
      });


    useEffect(() => {
        const fetchRateiCompany = async () => {
            setIsLoading(true);
            setError(null);
        
            try {
                const rateiCompanyData = await getRateosCompanyList(token);
                setRateiCompany(rateiCompanyData);
            } catch (error) {
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRateiCompany();
    }, [token]);

    useEffect(() => {
        if (!employeeId) {
            return;
        }
        getContractList();
    }, [employeeId]);

    const getContractList = function () {
        setIsLoading(true);
        setError(null);

        getEmployeeContractsList(token, employeeId, 'payrolls')
            .then(data => {
                setContracts(data);

                const activeContract = data.find(contract => contract.is_active);
                
                if (activeContract) {
                    setEmployeeContract(activeContract);
                    setActiveContractId(activeContract.id)
                } else {
                    setEmployeeContract(null);
                }
            })
            .catch(error => {
                setError(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        const fetchRateiEmployee = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const employeeRateiData = await getRateosEmployeeList(token, employeeId);
                setRateiEmployee(employeeRateiData);               
            } catch (error) {
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRateiEmployee();
    }, [token, employeeId]);

    
    const handleSelectRateo = (selected) => {
        setSelectedRateo(selected.rateo.name);
        setSelectedRateoId(selected.id);
    };

    useEffect(() => {
        if (selectedRateo) {
            const isRateoAlreadyActive = rateiEmployee.some(rateo => rateo.rateo.rateo.name === selectedRateo);

            if (isRateoAlreadyActive) {
                setIsRateoActive(true);
                setSelectedRateo(null);
                Swal.fire({
                    icon: "error",
                    text: "Il rateo selezionato è già attivo per questo dipendente",
                });
            } else {
                setIsRateoActive(false);
            }
        }
    }, [selectedRateo]);

    const formatDateString = (input) => {
        const cleaned = input.replace(/\D/g, '');

        if (cleaned.length > 2 && cleaned.length <= 4) {
            return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
        } else if (cleaned.length > 4) {
            return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
        }
        return cleaned;
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        return moment(dateString, 'YYYY-MM-DD').format('DD/MM/YYYY');
    };
    

    const isValidDateFormat = (dateString) => {
        return moment(dateString, 'DD/MM/YYYY', true).isValid();
    };
    
    const formatDateForBackend = (dateString) => {
        if (!dateString) return null;
        return moment(dateString, 'DD/MM/YYYY').format('YYYY-MM-DD');
    };
    
    const isDateValid = (startDate, endDate) => {
        const start = moment(startDate, 'DD/MM/YYYY');
        const end = moment(endDate, 'DD/MM/YYYY');
        return end.isAfter(start);
    };

    const convertDateToDisplayFormat = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    };    

    const convertToMinutes = (value) => {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return 0;
        return Math.floor(numericValue * 60);
    };
    
    const handleInputChange = (fieldName, value) => {
        let formattedValue = value;

        if (fieldName === 'dateFrom' || fieldName === 'dateTo') {
            formattedValue = formatDateString(value);
        }
    
        if (fieldName === 'monthly_increase') {
            formattedValue = value.replace(',', '.').replace(/[^0-9.]/g, '');
            const parts = formattedValue.split('.');
            if (parts.length > 2) {
                formattedValue = parts[0] + '.' + parts.slice(1).join('').substring(0, 2);
            } else if (parts.length === 2 && parts[1].length > 2) {
                formattedValue = parts[0] + '.' + parts[1].substring(0, 2);
            }
        }   

        setInputValues({
            ...inputValues,
            [fieldName]: formattedValue
        });
        setUnsavedChanges(true)
    };


    const handleSaveEmployeeRateo = async () => {
        const { dateFrom, dateTo, monthly_increase } = inputValues;
            
        if (!monthly_increase || isNaN(parseFloat(monthly_increase))) {
            setErrorCheck('Inserisci un valore valido per il coefficiente di maturazione');
            return;
        }
    
        if (!dateFrom || !isValidDateFormat(dateFrom)) {
            setErrorCheck('Inserisci una data di inizio valida nel formato gg/mm/aaaa');
            return;
        }
    
        if (dateTo && !isValidDateFormat(dateTo)) {
            setErrorCheck('Inserisci una data di fine valida nel formato gg/mm/aaaa');
            return;
        }
    
        if (dateTo && !isDateValid(dateFrom, dateTo)) {
            setErrorCheck('La data di fine deve essere maggiore della data di inizio');
            return;
        }
    
        const minutes = convertToMinutes(monthly_increase);
    
        try {
            setIsSaving(true);
    
            const requestData = {
                id: activeContractId,
                date_from: formatDateForBackend(dateFrom),
                date_to: formatDateForBackend(dateTo) || null,
                monthly_increase: minutes,
                is_counter_enable: true,
                rateo: selectedRateoId,
            };
    
            await createRateoEmployee(token, employeeId, requestData);
            Swal.fire({
                position: "center",
                icon: "success",
                title: "Salvato!",
                showConfirmButton: false,
                timer: 1500
            });
    
            const updatedRateiEmployee = await getRateosEmployeeList(token, employeeId);
            setRateiEmployee(updatedRateiEmployee);    
    
            setSelectedRateo(null);
            setInputValues(initialInputValues);
            setErrorCheck('');
            setShowForm(false);
            setUnsavedChanges(false)
    
        } catch (error) {
            console.error('Errore durante il salvataggio del rateo:', error);
            Swal.fire({
                icon: "error",
                title: "Oops...",
                text: 'Il salvataggio del rateo non è andato a buon fine: ' + error.message,
            });
        } finally {
            setIsSaving(false);
        }
    };

    function handleCancelSave() {
        setSelectedRateo(null);
        setSelectedRateoId(null);
        setErrorCheck('');
        setInputValues(initialInputValues);
        setUnsavedChanges(false);
    };

    function handleShowForm() {
        setShowForm(prevShowForm => !prevShowForm);
        setInputValues('');
        setSelectedRateo(null);
        setSelectedRateoId(null);
        setUnsavedChanges(false);
    };

    const handleDeleteRateoEmployee = async (employeeId, rateoId) => {
        try {
            const result = await Swal.fire({
                title: "Sei sicuro di voler eliminare il rateo?",
                showDenyButton: true,
                confirmButtonText: "Sì, elimina",
                denyButtonText: "Annulla",
                denyButtonColor: "#d6d3d1",
            });

            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Eliminazione in corso...',
                    didOpen: () => {
                        Swal.showLoading();
                    },
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    allowEnterKey: false,
                });

                await deleteRateoEmployee(token, employeeId, rateoId);

                Swal.fire({
                    title: "Rateo eliminato con successo!",
                    icon: "success",
                });

                const updatedRateiEmployee = await getRateosEmployeeList(token, employeeId);
                setRateiEmployee(updatedRateiEmployee);
            } else if (result.isDenied) {
                return;
            }
        } catch (error) {
            console.error("Errore durante l'eliminazione del rateo:", error);
            Swal.fire({
                title: "Errore durante l'eliminazione del rateo!",
                icon: "error",
            })};
    };

    const formatMinutesToHours = (minutes) => {
        const totalHours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
    
        if (totalHours === 0 && remainingMinutes === 0) {
            return '0 min';
        } else if (totalHours === 0) {
            return `${remainingMinutes} min / mese`;
        } else if (remainingMinutes === 0) {
            return `${totalHours} h / mese`;
        } else {
            return `${totalHours} h, ${remainingMinutes} min / mese`;
        }
    };

    useEffect(() => {
        if (editRateo) {
            setFormData({
                date_from: convertDateToDisplayFormat(editRateo.date_from),
                date_to: convertDateToDisplayFormat(editRateo.date_to),
                monthly_increase: editRateo.monthly_increase || 0,
            });
        }
    }, [editRateo]);
    
    const handleEditRateo = (rateo, rateoId) => {
        setEditRateo(rateo);
        setEditRateoId(rateoId);
        setFormData({
            date_from: formatDateForDisplay(rateo.date_from),
            date_to: rateo.date_to ? formatDateForDisplay(rateo.date_to) : '',
            monthly_increase: rateo.monthly_increase || 0, 
        });
        setShowFormEdit(true);
        setUnsavedChanges(true);
    };
    
    const formatMonthlyIncrease = (monthlyIncreaseValue) => {
        const numericValue = parseFloat(monthlyIncreaseValue.replace(',', '.'));
        if (!isNaN(numericValue)) {
            const minutes = Math.floor(numericValue * 60); 
            return minutes;
        } else {
            return '';
        }
    };
          
    
    const handleCancelEdit = () => {
        setErrorCheck('');
        setShowFormEdit(false);
        setUnsavedChanges(false);
    };

    const handleUpdateRateoEmployee = async () => {
        try {
          setErrorCheck('');
          setIsSaving(true);
      
        if (!editRateo || editRateoId === null) {
            throw new Error('ID del rateo non valido');
        }
      
        const { date_from, date_to, monthly_increase } = formData;
      
        if (date_to && !isDateValid(date_from, date_to)) {
            setErrorCheck('La data di fine deve essere maggiore della data di inizio');
            return;
        }

        if (!monthly_increase || isNaN(monthly_increase)) {
            setErrorCheck('Inserisci un valore valido per il coefficiente di maturazione');
            return;
        }
    
        if (!date_from || !isValidDateFormat(date_from)) {
            setErrorCheck('Inserisci una data di inizio valida nel formato gg/mm/aaaa');
            return;
        }
    
        if (date_to && !isValidDateFormat(date_to)) {
            setErrorCheck('Inserisci una data di fine valida nel formato gg/mm/aaaa');
            return;
        }

        const updatedData = {
            date_from: formatDateForBackend(date_from),
            date_to: date_to === '' ? null : formatDateForBackend(date_to),
            monthly_increase: Math.floor(monthly_increase),
        };
      
        await updateRateoEmployee(token, employeeId, editRateoId, updatedData);
      
        const updatedRateiEmployee = rateiEmployee.map(rateo => {
            if (rateo.id === editRateoId) {
                return { ...rateo, ...updatedData };
            }
            return rateo;
        });
      
        setRateiEmployee(updatedRateiEmployee);
        setEditRateo(null);
        setErrorCheck('');
        setShowFormEdit(false);
        setUnsavedChanges(false);
        Swal.fire({
            title: 'Salvato!',
            icon: 'success',
          });
        } catch (error) {
          console.error('Errore durante la modifica del rateo:', error);
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response?.data?.code?.join(', ') || 'Errore nel salvataggio',
          });
        } finally {
            setIsSaving(false);
        }
    };

      
    if(isLoading)
        return <div className="relative block w-full rounded-lg  p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2">
            <div role="status">
                <svg className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-grey-100 fill-indigo-700" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span className="sr-only">Loading...</span>
            </div>
            <span className="mt-2 block text-sm font-semibold text-gray-900">Caricamento...</span>
        </div>
    if (error) return <div>Error: {error.message}</div>;

    return (
        <div className='container px-4 pb-4 w-full' style={{ minHeight: '80vh', height: 'auto' }}>
            {activeContractId ? (
                <>
                    <div className='flex justify-end my-4 me-5'>
                        <button 
                            className="border border-gray-400 rounded-md bg-grey-400 me-1 px-3 py-2 text-center text-sm font-semibold text-grey-800 shadow-md hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-60"
                            onClick={handleShowForm}
                        >
                            {showForm ? <><ArrowUturnLeftIcon className="h-4 w-4 inline-block mr-1" /> Annulla</> : <><PlusIcon className="h-4 w-4 inline-block mr-2" /> Aggiungi Rateo</>}
                        </button>
                    </div>

                    {showForm && (
                        <div className='border shadow rounded-md px-4 mx-5 pt-6'>
                            <div className='grid grid-cols-5 mx-4 mb-10'>
                                <div className='col-span-1 flex items-center'>
                                    <h3 className='block text-sm font-medium text-gray-700 pe-8'>Seleziona il rateo</h3>
                                </div>
                                <div className='col-span-4  items-center w-full'>
                                    <Listbox value={selectedRateo} onChange={handleSelectRateo}>
                                        <div className="relative mt-1 w-full ml-6">
                                            <Listbox.Button className="relative cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-700 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-300 sm:text-sm w-full">
                                                <span className="block truncate">
                                                    {rateiCompany.length === 0 ? 'Nessun rateo presente' : (selectedRateo ? selectedRateo : 'Seleziona...')}
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                                                </span>
                                            </Listbox.Button>
                                            <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                            >
                                                <Listbox.Options className="absolute mt-1 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm w-full">
                                                    {rateiCompany.map((option) => {
                                                        const isRateoSaved = rateiEmployee.some(rateo => rateo.rateo.rateo.name === option.rateo.name);

                                                        return (
                                                            <Listbox.Option
                                                                key={option.id}
                                                                className={({ active }) =>
                                                                    `relative cursor-default select-none py-2 pl-7 pr-4 ${
                                                                        active ? 'bg-blue-100 text-blue-900' : isRateoSaved ? 'bg-gray-100' : 'text-gray-900'
                                                                    }`
                                                                }
                                                                value={option}
                                                            >
                                                                <div className="flex items-center">
                                                                    {isRateoSaved ? (
                                                                        <CheckIcon className='block h-3 w-3' />
                                                                    ) : (
                                                                        <span className='w-3'></span>
                                                                    )}
                                                                    <span className="ml-3">{option.rateo.name}</span>
                                                                </div>
                                                            </Listbox.Option>
                                                        );
                                                    })}
                                                </Listbox.Options>
                                            </Transition>
                                        </div>
                                    </Listbox>
                                </div>
                            </div>
                                                
                            {selectedRateoId && selectedRateo && !isRateoActive && (
                                <div className='grid grid-cols-2 sm:grid-cols-4 p-4 mt-5 justify-between items-center gap-6'>
                                    <div className='col col-span-2 sm:col-span-4'>
                                        <label htmlFor="rateo" className='block font-medium text-indigo-700 uppercase underline'>{selectedRateo}</label>
                                    </div>
                                  
                                    <div className="col col-span-1">
                                        <label htmlFor="monthly_increase" className='block text-sm font-medium text-gray-700'>Coefficiente di maturazione mensile</label>
                                    </div>
                                    <div className='col col-span-1 w-full'>
                                        <input
                                            type="text"
                                            value={inputValues.monthly_increase || ''}
                                            onChange={(e) => handleInputChange('monthly_increase', e.target.value)}
                                            className="border border-gray-100 shadow-md rounded-md px-2 py-1 w-full"
                                            min={0}
                                            placeholder="00,00 h"
                                            required
                                        />
                                    </div>

                                    <div className='col col-span-2 text-xs'>Inserire valore in ORE (0.50 corrisponde a 30 minuti)</div>
                                
                                    <div className='col col-span-1'>
                                        <label htmlFor="date_from" className='block text-sm font-medium text-gray-700'>Data Inizio *</label>
                                    </div>
                                    <div className='col col-span-1 w-full'>
                                        <input
                                            type="text"
                                            value={inputValues.dateFrom || ''}
                                            onChange={(e) => handleInputChange('dateFrom', e.target.value)}
                                            className="border border-gray-100 shadow-md rounded-md px-2 py-1 w-full"
                                            required
                                            placeholder='ggmmaaaa'
                                        />
                                    </div>
                            
                                    <div className='col col-span-1 justify-start sm:justify-center flex'>
                                        <label htmlFor="date_to" className='block text-sm font-medium text-gray-700'>Data Fine</label>
                                    </div>
                                    <div className='col col-span-1'>
                                        <input
                                            type="text"
                                            value={inputValues.dateTo || ''}
                                            onChange={(e) => handleInputChange('dateTo', e.target.value)}
                                            className="border border-gray-100 shadow-md rounded-md px-2 py-1 w-full"
                                            placeholder='ggmmaaaa'
                                        />
                                    </div>

                                    {errorCheck && <div className="col-span-4 text-red-500 text-sm flex justify-center">{errorCheck}</div>}
                
                                    <div className='col-start-2 col-span-1 flex justify-end'>
                                        <button
                                            type='submit'
                                            onClick={handleSaveEmployeeRateo}
                                            className={`bg-indigo-600 hover:bg-indigo-500 text-white font-semibold w-24 p-2 rounded-lg focus:outline-none focus:shadow-outline me-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'Salvataggio...' : 'Salva'}
                                        </button>
                                    </div>
                                    <div className='col col-span-1 flex justify-start'>
                                        <button 
                                            className='border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-md hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-24'
                                            onClick={handleCancelSave}
                                        >
                                            Annulla
                                        </button>
                                    </div>
                
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className="m-8 rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm font-semibold text-gray-900">
                    <div className="text-center p-5">
                        <p>Non sono presenti contratti attivi</p>
                    </div>
                </div>
            )}

            {rateiEmployee && activeContractId && (
                rateiEmployee
                .sort((a, b) => new Date(b.date_from).getTime() - new Date(a.date_from).getTime())
                .map((rateo) => (
                    <div key={rateo.id}>
                        {showFormEdit && editRateoId === rateo.id ? (
                            <div className='rounded px-8 pt-3 mb-4 m-6 border border-gray-400 shadow-md p-4'>
                                <div className='grid grid-cols-2'>
                                    <div className='col-span-1'>
                                        <span className='uppercase font-semibold text-indigo-700'>{rateo.rateo.rateo.name}</span>
                                    </div>
                                </div>
                                {errorCheck && (
                                    <div className="col-span-4 text-red-500 text-sm flex justify-center">{errorCheck}</div>
                                )}
                                <form onSubmit={(e) => {e.preventDefault();}}>
                                    <div className='grid grid-cols-4 gap-3 items-center mt-2'>
                                        <div className='col col-span-1'>
                                            <label htmlFor="date_from" className='block text-sm font-medium text-gray-700'>Data Inizio *</label>
                                        </div>
                                        <div className='col col-span-3'>
                                            <input
                                                type="text"
                                                id='date_from'
                                                name='date_from'
                                                value={formData.date_from}
                                                onChange={(e) => setFormData({ ...formData, date_from: formatDateString(e.target.value) })}
                                                className="border border-gray-100 shadow-md rounded-md px-2 py-1 w-full"
                                                placeholder='ggmmaaaa'
                                                required
                                            />
                                        </div>
                                        <div className='col col-span-1'>
                                            <label htmlFor="date_to" className='block text-sm font-medium text-gray-700'>Data Fine</label>
                                        </div>
                                        <div className='col col-span-3'>
                                            <input
                                                type="text"
                                                id='date_to'
                                                name='date_to'
                                                value={formData.date_to}
                                                onChange={(e) => setFormData({ ...formData, date_to: formatDateString(e.target.value) })}
                                                className="border border-gray-100 shadow-md rounded-md px-2 py-1 w-full"
                                                placeholder='ggmmaaaa'
                                            />
                                        </div>
                                        <div className="col col-span-1">
                                            <label htmlFor="monthly_increase" className='block text-sm font-medium text-gray-700'>Coefficiente di maturazione mensile</label>
                                        </div>
                                        <div className='col-span-3'>
                                            <input 
                                                type="text"
                                                defaultValue={(formData.monthly_increase / 60).toFixed(2)}
                                                onChange={(e) => setFormData({ ...formData, monthly_increase: formatMonthlyIncrease(e.target.value) })}
                                                className="border border-gray-100 shadow-md rounded-md px-2 py-1 w-full"
                                                min={0}
                                                placeholder="00,00 h"
                                                required 
                                            />
                                            <span className='text-xs'>Inserire valore in ORE (0.50 corrisponde a 30 minuti)</span>
                                        </div>
                                    </div>
                                </form>
                                <div className="col-span-3 mt-5 flex justify-center">
                                    <button
                                        type='button'
                                        className={`bg-indigo-700 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4 w-24 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        onClick={handleUpdateRateoEmployee}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Salvataggio...' : 'Salva'}
                                    </button>
                                    <button
                                        type="button" 
                                        className="border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-md hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-24"
                                        onClick={handleCancelEdit}
                                    >
                                        Annulla
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className='rounded px-8 pt-3 mb-4 m-6 border border-gray-400 shadow-md p-4'>
                                <div className='grid grid-cols-2'>
                                    <div className='col-span-1'>
                                        <span className='uppercase font-semibold text-indigo-700'>{rateo.rateo.rateo.name}</span>
                                    
                                    </div>
                                    <div className='col-span-1 justify-end flex'>
                                        <button 
                                            className="ml-2 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                            onClick={() => handleEditRateo(rateo, rateo.id)}
                                        >
                                            <PencilIcon className="h-4 w-4 inline-block mr-2" />
                                            Modifica
                                        </button>
                                        <button 
                                            className="ml-2 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-red-500"
                                            onClick={() => handleDeleteRateoEmployee(employeeId, rateo.id)}
                                        >
                                            <TrashIcon className="h-4 w-4 inline-block mr-2" />
                                            Elimina
                                        </button>
                                    </div>
                                </div>
                                <div className='grid grid-cols-3 gap-2 mt-2'>
                                    <div className='col-span-1'>
                                        <div className='font-semibold text-sm text-gray-700'>Data inizio</div>
                                    </div>
                                    <div className='col-span-2'>
                                        <div className='font-semibold text-sm text-gray-700'> {formatDate(rateo.date_from)}</div>
                                    </div>
                                    <div className='col-span-1'>
                                        <div className='font-semibold text-sm text-gray-700'>Data fine</div>
                                    </div>
                                    <div className='col-span-2'>
                                        <div className='font-semibold text-sm text-gray-700'>{rateo.date_to ? formatDate(rateo.date_to) : '-'}</div>
                                    </div>
                                    <div className='col-span-1'>
                                        <div className='font-semibold text-sm text-gray-700'>Coefficiente di maturazione</div>
                                    </div>
                                    <div className='col-span-2'>
                                        <div className='font-semibold text-sm text-gray-700'>{formatMinutesToHours(rateo.monthly_increase)}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};
